import { startLogin } from "@homebound/auth-components";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useViewport } from "src/contexts";
import { Button, Icon, NewFieldLabel, NewFieldStyleOverrides, PhoneField, REDIRECT_KEY, TextField } from "~components";
import { Css } from "~generated/css";
import { isValidEmail, isValidPhone, useTestIds } from "~utils";
import { AuthFormContainer } from "../components/AuthFormContainer";
import { AuthToggleLink } from "../components/AuthToggleLink";
import { ErrorMessage } from "../components/ErrorMessage";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { useSignUpHomeownerMutation } from "./SignUp.page.generated";

// starts with a landing page by defualt, then transitions to the sign up form that asks for email, name, phone
type SignupSteps = "landing" | "signUpForm";

type SignUpFormState = {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
};

export function SignUp() {
  const tid = useTestIds({}, "signUp");
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState<SignupSteps>(location.state?.step || "landing");
  // TODO: Could use a common form w add user modal and probably form state.
  // pass mutation fn in to that form to power both UIs
  const [form, setForm] = useState<SignUpFormState>({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [signUpHomeowner] = useSignUpHomeownerMutation();

  async function handleSignUpSubmit() {
    if (!isValidEmail(form.email)) {
      setError("Please enter a valid email address");
      return;
    }

    if (!form.firstName || !form.lastName) {
      setError("First name and last name are required");
      return;
    }
    if (form.phone && !isValidPhone(form.phone)) {
      setError("Phone number must be 10 digits");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const { errors } = await signUpHomeowner({
        variables: {
          input: {
            email: form.email,
            firstName: form.firstName,
            lastName: form.lastName,
            phone: form.phone || undefined,
          },
        },
      });
      if (errors) {
        setError("Something went wrong.");
        console.error("Sign up error");
      } else {
        // Users get logged in automatically after sign up and shown success screen
        const { data } = await startLogin(form.email);
        data && navigate("/enterCode", { state: { email: form.email, isSignUp: true } });
      }
    } catch {
      console.error("Sign up error");
      setError("Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  if (step === "landing") {
    return <SignUpLanding {...tid} setStep={setStep} />;
  }
  return (
    <SignUpForm
      {...tid}
      form={form}
      setForm={setForm}
      onSubmit={handleSignUpSubmit}
      // If we're coming from a QR code flow we want users to see the landing since they're in a model home
      // eventually that landing will have more content specific to the community they're in so we want no confusion.
      onBack={() => {
        const isQrSignUp = localStorage.getItem(REDIRECT_KEY)?.includes("/qr");
        if (isQrSignUp) {
          setStep("landing");
        } else {
          navigate("/signIn");
        }
      }}
      error={error}
      loading={loading}
    />
  );
}

type SignUpFormProps = {
  error: string | null;
  onBack: () => void;
  form: SignUpFormState;
  setForm: (form: SignUpFormState) => void;
  onSubmit: () => void;
  loading: boolean;
};

function SignUpForm(props: SignUpFormProps) {
  const { form, setForm, onSubmit, onBack, error, loading } = props;
  const { isMobile } = useViewport();
  const tid = useTestIds(props, "signUpForm");

  return (
    <AuthFormContainer title="Sign Up" onBack={onBack} {...tid}>
      <div css={isMobile ? Css.df.fdc.gap3.h100.px4.pt4.$ : Css.df.fdc.gap3.w100.$}>
        {!isMobile && <img src="/logo.svg" width="153" height="26" alt="Homebound" css={Css.mxa.$} />}
        <div css={Css.w100.$}>
          <TextField
            {...tid.email}
            placeholder="Email@email.com"
            cssOverrides={NewFieldStyleOverrides}
            label={NewFieldLabel("Email*")}
            type="email"
            value={form.email}
            onChange={(_, email) => setForm({ ...form, email })}
            autoComplete="email"
            required
          />
        </div>
        <div css={Css.w100.$}>
          <TextField
            {...tid.firstName}
            cssOverrides={NewFieldStyleOverrides}
            label={NewFieldLabel("First Name*")}
            type="text"
            placeholder="First Name"
            value={form.firstName}
            onChange={(_, firstName) => setForm({ ...form, firstName })}
            autoComplete="given-name"
            required
          />
        </div>
        <div css={Css.w100.$}>
          <TextField
            {...tid.lastName}
            cssOverrides={NewFieldStyleOverrides}
            label={NewFieldLabel("Last Name*")}
            type="text"
            placeholder="Last Name"
            value={form.lastName}
            onChange={(_, lastName) => setForm({ ...form, lastName })}
            autoComplete="family-name"
            required
          />
        </div>
        <div css={Css.w100.$}>
          <PhoneField
            {...tid.phone}
            value={form.phone}
            onChange={(phone) => setForm({ ...form, phone })}
            placeholder="(XXX) XXX-XXXX"
            useConfigurationStyle={true}
          />
        </div>
        {error && <ErrorMessage message={error} />}
        <Button
          {...tid.submitButton}
          variant="primary"
          disabled={!form.firstName || !form.lastName || loading}
          onClick={onSubmit}
          isLoading={loading}
          xss={Css.ifMobile.w100.$}
        >
          Sign Up
        </Button>
        <div css={Css.t12.gray.$}>
          By submitting this form, you're authorizing Homebound to contact you via your preferred contact method, in
          accordance with our{" "}
          <a css={Css.black.$} href="https://www.homebound.com/terms-of-use" target="_blank" rel="noreferrer">
            Terms of Use
          </a>{" "}
          and{" "}
          <a css={Css.black.$} href="https://www.homebound.com/privacy-policy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          . If you provide us with your phone number, you consent to receiving communications from Homebound by call or
          text and confirm that you have the authority to consent to receive such communication.
        </div>
        {loading && <LoadingOverlay />}
      </div>
    </AuthFormContainer>
  );
}

type SignUpLandingProps = {
  setStep: (step: "signUpForm") => void;
};

function SignUpLanding(props: SignUpLandingProps) {
  const { setStep } = props;
  const { isMobile, isDesktop } = useViewport();
  const tid = useTestIds(props, "signUpLanding");

  // Desktop is a 2 column layout with just the hero centered left and content centered right just a hair differently
  if (isDesktop) {
    return (
      <>
        {/* Cheating and absolute positioning this logo to the top left corner of the screen */}
        <div css={Css.absolute.top0.left0.p5.$}>
          <img src="/logo.svg" width="153" height="26" alt="Homebound" />
        </div>
        <div css={Css.df.vh100.w100.df.$}>
          {/* Left */}
          <div css={Css.w100.h100.df.jcc.aic.$}>
            <img src="/signUpHero.png" alt="Sign up hero" width="426" height="368" />
          </div>
          {/* Right */}
          <div css={Css.w100.h100.df.fdc.jcc.aic.gap5.$}>
            <div css={Css.t32.gray800.tac.$}>Start your interactive tour</div>
            <div css={Css.t18.gray.tac.$}>
              Sign up to personalize this home
              <br />
              in real time.
            </div>
            <SignUpLandingActions {...tid} setStep={setStep} />
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      css={isMobile ? Css.relative.vh100.w100.df.fdc.jcse.aic.tac.$ : Css.df.fdc.jcc.aic.vh100.gap3.wPx(500).mxa.$}
      {...tid}
    >
      {/* Logo and title */}
      {/* Top margin is percentage based on figma design. (top margin) / (height - status bar) */}
      <div css={Css.mt("3%").df.fdc.gap(2.5).$}>
        <img
          src="/logo.svg"
          width={isMobile ? "126" : "153"}
          height={isMobile ? "20" : "26"}
          alt="Homebound"
          css={Css.mxa.$}
        />
        <div css={Css.t32.gray800.$}>Start your interactive tour</div>
      </div>
      {/* Hero image and text */}
      <div css={Css.df.fdc.gap5.$}>
        <img
          src="/signUpHero.png"
          alt="Sign up hero"
          width={isMobile ? "297" : "426"}
          height={isMobile ? "255" : "368"}
        />
        <div css={Css.t18.gray.tac.$}>
          Sign up to personalize this home
          <br />
          in real time.
        </div>
      </div>
      {/* Sign up and go to sign in */}
      <SignUpLandingActions {...tid} setStep={setStep} />
    </div>
  );
}

function SignUpLandingActions(props: SignUpLandingProps) {
  const { setStep } = props;
  const tid = useTestIds(props, "landingActions");

  return (
    // Bottom margin is percentage based on figma design. (bottom margin) / (height - status bar)
    <div css={Css.df.fdc.gap4.wPx(329).ifTabletAndDown.mb("12%").$}>
      <Button {...tid.signUpButton} variant="primary" onClick={() => setStep("signUpForm")}>
        {/* Our icons are just a littttle jank so for px perfect alignment we'll fanaggle it a bit */}
        <span css={Css.mr1.ptPx(2).addIn("> svg > path", Css.fWhite.$).$}>
          <Icon name="email" />
        </span>
        Sign up
      </Button>
      <div>
        <AuthToggleLink mode="signUp" />
      </div>
    </div>
  );
}
